import React, { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { useHistoricalPlaybackStore } from '../../store/historicalPlaybackStore';
import { useZoneStore } from '../../store/zoneStore';
import { parseUtcTimestampMs } from '../../utils/timeUtils';

const STEP_MS = 15 * 60 * 1000; // snapshot raster (15 min)
const SCRUB_DEBOUNCE_MS = 250;

function formatInstant(ms: number): string {
  return new Date(ms).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Map overlay for historical powerflow playback (time-rewind).
 *
 * Live mode: a single "Verlauf" button (only when stored snapshots exist).
 * Historical mode: a prominent banner (the map no longer shows live data!)
 * plus a scrubber over the available snapshot range. All data flows through
 * historicalPlaybackStore — map components render unchanged.
 */
export const MapOverlay: React.FC = () => {
  const mode = useHistoricalPlaybackStore(state => state.mode);
  const range = useHistoricalPlaybackStore(state => state.range);
  const selectedTs = useHistoricalPlaybackStore(state => state.selectedTs);
  const effectiveTs = useHistoricalPlaybackStore(state => state.effectiveTs);
  const isLoading = useHistoricalPlaybackStore(state => state.isLoading);
  const error = useHistoricalPlaybackStore(state => state.error);
  const setTimestamp = useHistoricalPlaybackStore(state => state.setTimestamp);
  const backToLive = useHistoricalPlaybackStore(state => state.backToLive);
  const checkFrame = useHistoricalPlaybackStore(state => state.checkFrame);
  const showCheckFrame = useHistoricalPlaybackStore(state => state.showCheckFrame);
  const loadRange = useHistoricalPlaybackStore(state => state.loadRange);
  const activeZoneId = useZoneStore(state => state.activeZoneId);
  // Archive zone (inactive + snapshots): there is no "live" to return to —
  // the banner offers "Zum Archiv-Ende" instead of a dead "Zurück zu Live".
  const isArchiveZone = useZoneStore(state =>
    state.zones.find(z => z.id === state.activeZoneId)?.isActive === false);

  // Slider position while scrubbing (ms epoch); commit is debounced.
  const [scrubMs, setScrubMs] = useState<number | null>(null);
  // The overlay lives INSIDE the Leaflet container: without this, every click
  // on the slider/buttons bubbles to the map, whose click handler deselects the
  // element and closes the detail panel — scrubbing the time then always
  // kicked the user out of the element they were comparing.
  const isolateFromMap = useCallback((el: HTMLDivElement | null) => {
    if (el) {
      L.DomEvent.disableClickPropagation(el);
      L.DomEvent.disableScrollPropagation(el);
    }
  }, []);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial range load for the already-selected zone (zone switches are
  // handled by the store's zoneStore subscription).
  useEffect(() => {
    if (activeZoneId) {
      void loadRange();
    }
  }, [activeZoneId, loadRange]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  // Netzanschluss check frame (Prüffall): the map shows the with-assets load
  // flow of a check — not live, not an archived instant. Same banner slot as
  // the rewind; the scrubber is irrelevant here.
  if (mode === 'check') {
    const direction = checkFrame?.direction;
    const displayOnly = direction != null && (checkFrame?.displayOnly ?? []).includes(direction);
    const leave = () => {
      if (isArchiveZone && range) {
        void setTimestamp(range.to);
      } else {
        backToLive();
      }
    };
    return (
      <div ref={isolateFromMap} className="absolute top-0 left-0 right-0 z-1001 px-4 py-2 bg-linear-to-r from-sky-600/95 to-blue-600/95 backdrop-blur-xs shadow-md">
        <div className="flex items-center justify-between gap-3 text-white">
          <span className="text-sm font-medium truncate">
            Prüffall — {checkFrame?.label ?? 'Netzanschlussprüfung'}
            <span className="hidden md:inline font-normal opacity-90">
              {displayOnly
                ? ' · Netzzustand dieser Richtung mit dem neuen Anschluss — die beantragte Anlage wirkt hier nicht (kein Kriterium)'
                : ' · Karte zeigt den Lastfluss mit der beantragten Anlage'}
              {isLoading ? ' (lädt …)' : ''}
            </span>
          </span>
          <div className="flex items-center gap-2 shrink-0">
            {checkFrame && checkFrame.available.length > 1 && (
              <div className="flex rounded-md overflow-hidden text-xs font-semibold">
                {checkFrame.available.map(d => (
                  <button
                    key={d}
                    onClick={() => { if (d !== direction) void showCheckFrame(checkFrame.requestId, checkFrame.label, d).catch(() => undefined); }}
                    className={`px-2.5 py-1 cursor-pointer ${d === direction ? 'bg-white text-blue-700' : 'bg-white/20 hover:bg-white/30'}`}
                    title={(d === 'feedIn' ? 'Einspeise-Worst-Case (Schwachlast + Erzeugung)' : 'Starklast-Worst-Case (Spitzenlast, keine PV)')
                      + (checkFrame.displayOnly.includes(d) ? ' — nur Anzeige, die Anlage wirkt in dieser Richtung nicht' : '')}
                  >
                    {d === 'feedIn' ? 'Einspeisung' : 'Starklast'}{checkFrame.displayOnly.includes(d) ? ' ⓘ' : ''}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={leave}
              className="px-3 py-1 rounded-md text-sm font-semibold bg-white/20 hover:bg-white/30 cursor-pointer"
            >
              {isArchiveZone && range ? 'Zurück zum Archiv' : 'Zurück zu Live'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!range) {
    return null;
  }

  const fromMs = parseUtcTimestampMs(range.from);
  const toMs = parseUtcTimestampMs(range.to);
  const selectedMs = scrubMs ?? (selectedTs ? parseUtcTimestampMs(selectedTs) : toMs);

  const commitTimestamp = (ms: number) => {
    const clamped = Math.min(Math.max(ms, fromMs), toMs);
    void setTimestamp(new Date(clamped).toISOString());
  };

  const onScrub = (ms: number) => {
    setScrubMs(ms);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setScrubMs(null);
      commitTimestamp(ms);
    }, SCRUB_DEBOUNCE_MS);
  };

  const step = (deltaMs: number) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setScrubMs(null);
    commitTimestamp(selectedMs + deltaMs);
  };

  if (mode === 'live') {
    return (
      <div ref={isolateFromMap} className="absolute bottom-4 left-1/2 -translate-x-1/2 z-1000">
        <button
          onClick={() => commitTimestamp(toMs)}
          className="px-3 py-1.5 rounded-lg shadow-lg text-sm font-medium bg-white/95 dark:bg-primary-800/95 backdrop-blur-xs border border-gray-200 dark:border-primary-600 text-gray-800 dark:text-primary-100 hover:bg-gray-50 dark:hover:bg-primary-700 cursor-pointer"
          title="Gespeicherte historische Lastfluss-Ergebnisse ansehen"
        >
          ⏱ Verlauf
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Historical-mode banner: the map does NOT show live data */}
      <div ref={isolateFromMap} className="absolute top-0 left-0 right-0 z-1001 px-4 py-2 bg-linear-to-r from-orange-500/95 to-amber-500/95 backdrop-blur-xs shadow-md">
        <div className="flex items-center justify-between gap-3 text-white">
          <span className="text-sm font-medium truncate">
            {isArchiveZone ? 'Archiv' : 'Historische Ansicht'} — {effectiveTs ? `${formatInstant(parseUtcTimestampMs(effectiveTs))} Uhr` : 'lädt …'}
            <span className="hidden md:inline font-normal opacity-90">
              {isArchiveZone
                ? ` · gespeicherte Lastfluss-Ergebnisse, Zeitraum ${formatInstant(fromMs).slice(0, 10)} – ${formatInstant(toMs).slice(0, 10)}`
                : ' · Karte zeigt gespeicherte Lastfluss-Ergebnisse'}
            </span>
          </span>
          {isArchiveZone ? (
            <button
              onClick={() => commitTimestamp(toMs)}
              disabled={selectedMs >= toMs}
              className="shrink-0 px-3 py-1 rounded-md text-sm font-semibold bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-default cursor-pointer"
              title="Zum letzten archivierten Zeitpunkt springen"
            >
              Zum Archiv-Ende
            </button>
          ) : (
            <button
              onClick={backToLive}
              className="shrink-0 px-3 py-1 rounded-md text-sm font-semibold bg-white/20 hover:bg-white/30 cursor-pointer"
            >
              Zurück zu Live
            </button>
          )}
        </div>
      </div>

      {/* Scrubber */}
      <div ref={isolateFromMap} className="absolute bottom-4 left-1/2 -translate-x-1/2 z-1000 w-[min(42rem,calc(100%-2rem))] px-4 py-2.5 rounded-lg shadow-lg bg-white/95 dark:bg-primary-800/95 backdrop-blur-xs border border-gray-200 dark:border-primary-600">
        <div className="flex items-center gap-2">
          <button
            onClick={() => step(-24 * 60 * 60 * 1000)}
            className="px-1.5 py-0.5 rounded text-xs font-mono text-gray-600 dark:text-primary-300 hover:bg-gray-100 dark:hover:bg-primary-700 cursor-pointer"
            title="1 Tag zurück"
          >
            −1T
          </button>
          <button
            onClick={() => step(-STEP_MS)}
            className="px-1.5 py-0.5 rounded text-xs font-mono text-gray-600 dark:text-primary-300 hover:bg-gray-100 dark:hover:bg-primary-700 cursor-pointer"
            title="15 Minuten zurück"
          >
            −15m
          </button>
          <input
            type="range"
            min={fromMs}
            max={toMs}
            step={STEP_MS}
            value={selectedMs}
            onChange={e => onScrub(Number(e.target.value))}
            className="flex-1 accent-accent-primary cursor-pointer"
            aria-label="Historischen Zeitpunkt wählen"
          />
          <button
            onClick={() => step(STEP_MS)}
            className="px-1.5 py-0.5 rounded text-xs font-mono text-gray-600 dark:text-primary-300 hover:bg-gray-100 dark:hover:bg-primary-700 cursor-pointer"
            title="15 Minuten vor"
          >
            +15m
          </button>
          <button
            onClick={() => step(24 * 60 * 60 * 1000)}
            className="px-1.5 py-0.5 rounded text-xs font-mono text-gray-600 dark:text-primary-300 hover:bg-gray-100 dark:hover:bg-primary-700 cursor-pointer"
            title="1 Tag vor"
          >
            +1T
          </button>
          <input
            type="datetime-local"
            value={(() => {
              // datetime-local speaks browser wall-clock — deliberate: the user
              // picks the local time they want to inspect.
              const d = new Date(selectedMs);
              const pad = (n: number) => String(n).padStart(2, '0');
              return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
            })()}
            onChange={e => {
              const ms = new Date(e.target.value).getTime();
              if (Number.isFinite(ms)) commitTimestamp(ms);
            }}
            className="ml-1 px-1 py-0.5 rounded border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-700 text-xs text-gray-700 dark:text-primary-200"
            aria-label="Datum und Uhrzeit wählen"
          />
        </div>
        <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500 dark:text-primary-400">
          <span>{formatInstant(fromMs)}</span>
          <span className="font-mono font-semibold text-gray-700 dark:text-primary-200">
            {isLoading ? 'lädt …' : error ? error : `${formatInstant(selectedMs)} Uhr`}
          </span>
          <span>{formatInstant(toMs)}</span>
        </div>
      </div>
    </>
  );
};
